import React, { useState } from 'react';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Download, RefreshCw, Trash2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';

// ─── Sync Diagnostic Component ────────────────────────────────────────────────
function SyncDiagnostic() {
    const [loading,    setLoading]    = useState(false);
    const [resyncing,  setResyncing]  = useState(false);
    const [diagnostic, setDiagnostic] = useState(null);
    const [error,      setError]      = useState(null);

    const today      = format(new Date(), 'yyyy-MM-dd');
    const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');

    const [startDate, setStartDate] = useState(monthStart);
    const [endDate,   setEndDate]   = useState(today);

    const runDiagnostic = async () => {
        setLoading(true);
        setError(null);
        try {
            const fn = httpsCallable(functions, 'getDailySyncDiagnostic');
            const res = await fn({ startDate, endDate });
            setDiagnostic(res.data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const runResync = async () => {
        if (!window.confirm(`¿Forzar re-sincronización de ${startDate} a ${endDate}? Esto descargará TODOS los registros de DynamiaERP.`)) return;
        setResyncing(true);
        setError(null);
        try {
            const fn = httpsCallable(functions, 'resyncDays', { timeout: 540000 });
            const res = await fn({ startDate, endDate });
            alert(`✅ ${res.data.message}`);
            await runDiagnostic();
        } catch (e) {
            setError(e.message);
        } finally {
            setResyncing(false);
        }
    };

    const s = diagnostic?.summary;

    return (
        <Card className="bg-slate-900 border-white/10 text-white mb-6">
            <CardHeader>
                <CardTitle className="text-lg">🔬 Diagnóstico de Sincronización Incremental</CardTitle>
                <p className="text-white/50 text-sm">Verifica qué días están guardados en Firebase RTDB y detecta datos faltantes.</p>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Controls */}
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="text-xs text-white/50 block mb-1">Desde</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm" />
                    </div>
                    <div>
                        <label className="text-xs text-white/50 block mb-1">Hasta</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm" />
                    </div>
                    <Button onClick={runDiagnostic} disabled={loading} className="bg-blue-600 hover:bg-blue-500">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Diagnosticar
                    </Button>
                    <Button onClick={runResync} disabled={resyncing || loading} variant="outline"
                        className="border-orange-500/40 text-orange-300 hover:bg-orange-950/30">
                        {resyncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Forzar Re-Sync
                    </Button>
                </div>

                {error && (
                    <div className="bg-red-950/30 border border-red-500/30 rounded p-3 text-red-300 text-sm">{error}</div>
                )}

                {/* Summary */}
                {s && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Días en rango', value: s.totalDays },
                            { label: 'Días sincronizados', value: s.syncedDays, color: s.syncedDays === s.totalDays ? 'text-green-400' : 'text-yellow-400' },
                            { label: 'Órdenes esperadas', value: s.totalExpected },
                            { label: 'Órdenes en RTDB', value: s.totalRtdb, color: s.mismatch ? 'text-red-400' : 'text-green-400' },
                        ].map(({ label, value, color }) => (
                            <div key={label} className="bg-white/5 rounded p-3">
                                <p className="text-xs text-white/50 uppercase">{label}</p>
                                <p className={`text-2xl font-bold ${color || 'text-white'}`}>{value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Alert if mismatch */}
                {s?.mismatch && (
                    <div className="flex items-start gap-2 bg-yellow-950/30 border border-yellow-500/30 rounded p-3 text-yellow-300 text-sm">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <div>
                            <strong>Datos incompletos:</strong> Hay {s.totalExpected - s.totalRtdb} órdenes esperadas que no están en RTDB.
                            Esto causa que el dashboard muestre cifras incorrectas. Usa "Forzar Re-Sync" para corregirlo.
                        </div>
                    </div>
                )}

                {/* Day-by-day table */}
                {diagnostic?.days && (
                    <div className="overflow-x-auto max-h-96 border border-white/10 rounded">
                        <table className="w-full text-xs">
                            <thead className="bg-white/10 sticky top-0">
                                <tr>
                                    <th className="p-2 text-left text-white/60">Fecha</th>
                                    <th className="p-2 text-left text-white/60">Estado</th>
                                    <th className="p-2 text-right text-white/60">Esperadas</th>
                                    <th className="p-2 text-right text-white/60">En RTDB</th>
                                    <th className="p-2 text-left text-white/60">Muestra</th>
                                </tr>
                            </thead>
                            <tbody>
                                {diagnostic.days.map(day => (
                                    <tr key={day.date} className={`border-t border-white/5 ${!day.syncStatus.includes('synced') ? 'bg-red-950/20' : day.expectedCount !== day.rtdbCount ? 'bg-yellow-950/20' : ''}`}>
                                        <td className="p-2 font-mono text-white">{day.date}</td>
                                        <td className="p-2">
                                            {day.syncStatus.includes('✅') ? <CheckCircle className="h-3 w-3 text-green-400 inline" /> : <XCircle className="h-3 w-3 text-red-400 inline" />}
                                            <span className="ml-1 text-white/70">{day.syncStatus}</span>
                                        </td>
                                        <td className="p-2 text-right text-white/60">{day.expectedCount}</td>
                                        <td className={`p-2 text-right font-bold ${day.expectedCount !== day.rtdbCount ? 'text-red-400' : 'text-green-400'}`}>{day.rtdbCount}</td>
                                        <td className="p-2 text-white/40 truncate max-w-[200px]">
                                            {day.sampleOrder ? `#${day.sampleOrder.numero} $${day.sampleOrder.subtotal} - ${day.sampleOrder.vendedor}` : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function DebugDynamiaERPPage() {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState({
        bodegas: null,
        existencias: null,
        stockByBranch: null,
        transfers: null,
        stockFromTransfers: null,
        movimientos: null,
        exhaustiveSearch: null,
        testVariaciones: null,
        completeSync: null
    });

    const exploreBodegas = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling exploreBodegas...');
            const fn = httpsCallable(functions, 'exploreBodegas');
            const result = await fn();
            console.log('[Debug] Bodegas result:', result.data);
            setResults(prev => ({ ...prev, bodegas: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, bodegas: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const exploreExistencias = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling exploreExistencias...');
            const fn = httpsCallable(functions, 'exploreExistencias');
            const result = await fn();
            console.log('[Debug] Existencias result:', result.data);
            setResults(prev => ({ ...prev, existencias: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, existencias: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const fetchStockByBranch = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling fetchStockByBranch...');
            const fn = httpsCallable(functions, 'fetchStockByBranch');
            const result = await fn();
            console.log('[Debug] Stock by branch result:', result.data);
            setResults(prev => ({ ...prev, stockByBranch: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, stockByBranch: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const exploreTransfers = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling exploreTransfers...');
            const fn = httpsCallable(functions, 'exploreTransfers');
            const result = await fn();
            console.log('[Debug] Transfers result:', result.data);
            setResults(prev => ({ ...prev, transfers: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, transfers: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const calculateStockFromTransfers = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling calculateStockFromTransfers...');
            const fn = httpsCallable(functions, 'calculateStockFromTransfers');
            const result = await fn({ days: 90 });
            console.log('[Debug] Stock from transfers result:', result.data);
            setResults(prev => ({ ...prev, stockFromTransfers: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, stockFromTransfers: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const exploreMovimientos = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling exploreMovimientos...');
            const fn = httpsCallable(functions, 'exploreMovimientos');
            const result = await fn();
            console.log('[Debug] Movimientos result:', result.data);
            setResults(prev => ({ ...prev, movimientos: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, movimientos: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const exhaustiveInventorySearch = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling exhaustiveInventorySearch...');
            const fn = httpsCallable(functions, 'exhaustiveInventorySearch');
            const result = await fn();
            console.log('[Debug] Exhaustive search result:', result.data);
            setResults(prev => ({ ...prev, exhaustiveSearch: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, exhaustiveSearch: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const testExistenciasVariaciones = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling testExistenciasDetalladas...');
            const fn = httpsCallable(functions, 'testExistenciasDetalladas');
            const result = await fn();
            console.log('[Debug] Test variaciones result:', result.data);
            setResults(prev => ({ ...prev, testVariaciones: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, testVariaciones: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const syncCompleteInventory = async () => {
        setLoading(true);
        try {
            console.log('[Debug] Calling syncCompleteInventory - This will take 2-3 minutes...');
            const fn = httpsCallable(functions, 'syncCompleteInventory');
            const result = await fn();
            console.log('[Debug] Complete sync result:', result.data);
            setResults(prev => ({ ...prev, completeSync: result.data }));
        } catch (error) {
            console.error('[Debug] Error:', error);
            setResults(prev => ({ ...prev, completeSync: { error: error.message } }));
        } finally {
            setLoading(false);
        }
    };

    const exportJSON = (data, filename) => {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-white p-8">
            <div className="max-w-7xl mx-auto space-y-6">
                <div>
                    <h1 className="text-3xl font-bold mb-2">🔍 Debug DynamiaERP API</h1>
                    <p className="text-slate-400">Diagnóstico de sincronización y exploración de endpoints</p>
                </div>

                {/* Sync Diagnostic - shown first */}
                <SyncDiagnostic />

                {/* Control Panel */}
                <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                        <CardTitle>Ejecutar Exploraciones</CardTitle>
                    </CardHeader>
                    <CardContent className="flex gap-4">
                        <Button
                            onClick={exploreBodegas}
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            1. Explorar Bodegas
                        </Button>
                        <Button
                            onClick={exploreExistencias}
                            disabled={loading}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            2. Explorar Existencias
                        </Button>
                        <Button
                            onClick={fetchStockByBranch}
                            disabled={loading}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            3. Stock por Sucursal
                        </Button>
                        <Button
                            onClick={exploreTransfers}
                            disabled={loading}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            4. Explorar Traslados
                        </Button>
                        <Button
                            onClick={calculateStockFromTransfers}
                            disabled={loading}
                            className="bg-pink-600 hover:bg-pink-700"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            5. Calcular Stock desde Traslados
                        </Button>
                        <Button
                            onClick={exploreMovimientos}
                            disabled={loading}
                            className="bg-yellow-600 hover:bg-yellow-700"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            6. Explorar Movimientos
                        </Button>
                        <Button
                            onClick={exhaustiveInventorySearch}
                            disabled={loading}
                            className="bg-red-600 hover:bg-red-700 font-bold"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            🔍 7. BÚSQUEDA EXHAUSTIVA
                        </Button>
                        <Button
                            onClick={testExistenciasVariaciones}
                            disabled={loading}
                            className="bg-purple-600 hover:bg-purple-700 font-bold text-lg"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            🧪 8. TEST VARIACIONES EXISTENCIAS
                        </Button>
                        <Button
                            onClick={syncCompleteInventory}
                            disabled={loading}
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 font-bold text-xl border-2 border-green-400"
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                            🔄 9. SYNC COMPLETO (Todas las 99 páginas)
                        </Button>
                    </CardContent>
                </Card>

                {/* Results */}
                <div className="grid grid-cols-1 gap-6">
                    {/* Bodegas Result */}
                    {results.bodegas && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>📦 Resultado: Bodegas</CardTitle>
                                <Button
                                    onClick={() => exportJSON(results.bodegas, 'bodegas')}
                                    className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                                >
                                    <Download className="w-3 h-3 mr-1" />
                                    Exportar JSON
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                    {JSON.stringify(results.bodegas, null, 2)}
                                </pre>
                            </CardContent>
                        </Card>
                    )}

                    {/* Existencias Result */}
                    {results.existencias && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>📊 Resultado: Existencias</CardTitle>
                                <Button
                                    onClick={() => exportJSON(results.existencias, 'existencias')}
                                    className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                                >
                                    <Download className="w-3 h-3 mr-1" />
                                    Exportar JSON
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                    {JSON.stringify(results.existencias, null, 2)}
                                </pre>
                            </CardContent>
                        </Card>
                    )}

                    {/* Stock by Branch Result */}
                    {results.stockByBranch && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>🏢 Resultado: Stock por Sucursal</CardTitle>
                                <Button
                                    onClick={() => exportJSON(results.stockByBranch, 'stock_por_sucursal')}
                                    className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                                >
                                    <Download className="w-3 h-3 mr-1" />
                                    Exportar JSON
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <span className="text-slate-400">Sucursales:</span>
                                            <p className="font-bold text-lg">{results.stockByBranch.branches?.length || 0}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Total Items:</span>
                                            <p className="font-bold text-lg">{results.stockByBranch.totalItems || 0}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Items Procesados:</span>
                                            <p className="font-bold text-lg">{results.stockByBranch.itemsProcessed || 0}</p>
                                        </div>
                                    </div>
                                    <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                        {JSON.stringify(results.stockByBranch, null, 2)}
                                    </pre>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Transfers Result */}
                    {results.transfers && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>🚚 Resultado: Exploración de Traslados</CardTitle>
                                <Button
                                    onClick={() => exportJSON(results.transfers, 'traslados')}
                                    className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                                >
                                    <Download className="w-3 h-3 mr-1" />
                                    Exportar JSON
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="text-sm text-slate-400">
                                        <p>Timestamp: {results.transfers.timestamp}</p>
                                        <p>Endpoints explorados: {Object.keys(results.transfers.endpoints || {}).length}</p>
                                    </div>
                                    <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                        {JSON.stringify(results.transfers, null, 2)}
                                    </pre>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Stock from Transfers Result */}
                    {results.stockFromTransfers && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>📦 Resultado: Stock Calculado desde Traslados</CardTitle>
                                <Button
                                    onClick={() => exportJSON(results.stockFromTransfers, 'stock_desde_traslados')}
                                    className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                                >
                                    <Download className="w-3 h-3 mr-1" />
                                    Exportar JSON
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <span className="text-slate-400">Sucursales:</span>
                                            <p className="font-bold text-lg">{results.stockFromTransfers.branches?.length || 0}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Productos:</span>
                                            <p className="font-bold text-lg">{results.stockFromTransfers.productsCalculated || 0}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Traslados:</span>
                                            <p className="font-bold text-lg">{results.stockFromTransfers.transfersProcessed || 0}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Items Movidos:</span>
                                            <p className="font-bold text-lg">{results.stockFromTransfers.itemsProcessed || 0}</p>
                                        </div>
                                    </div>
                                    <div className="text-sm">
                                        <p className="text-slate-400">Rango de fechas:</p>
                                        <p className="text-white">{results.stockFromTransfers.dateRange?.start} - {results.stockFromTransfers.dateRange?.end}</p>
                                    </div>
                                    {results.stockFromTransfers.stockSummary && (
                                        <div>
                                            <p className="text-slate-400 text-sm mb-2">Stock por sucursal:</p>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                {Object.entries(results.stockFromTransfers.stockSummary).map(([branch, count]) => (
                                                    <div key={branch} className="bg-black/40 p-2 rounded">
                                                        <span className="text-slate-400">{branch}:</span>
                                                        <span className="text-white ml-2 font-bold">{count} productos</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                        {JSON.stringify(results.stockFromTransfers, null, 2)}
                                    </pre>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Movimientos Result */}
                {results.movimientos && (
                    <Card className="bg-white/5 border-white/10">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>🔄 Resultado: Movimientos de Inventario</CardTitle>
                            <Button
                                onClick={() => exportJSON(results.movimientos, 'movimientos')}
                                className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                            >
                                <Download className="w-3 h-3 mr-1" />
                                Exportar JSON
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-slate-400">Total Registros:</span>
                                        <p className="font-bold text-lg">{results.movimientos.totalCount || 0}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Estructura:</span>
                                        <p className="text-xs text-slate-300">{results.movimientos.structure?.join(', ')}</p>
                                    </div>
                                </div>
                                <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                    {JSON.stringify(results.movimientos, null, 2)}
                                </pre>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Exhaustive Search Result */}
                {results.exhaustiveSearch && (
                    <Card className="bg-white/5 border-white/10">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>🔍 Resultado: Búsqueda Exhaustiva de Inventario</CardTitle>
                            <Button
                                onClick={() => exportJSON(results.exhaustiveSearch, 'exhaustive_search')}
                                className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                            >
                                <Download className="w-3 h-3 mr-1" />
                                Exportar JSON
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {results.exhaustiveSearch.summary && (
                                    <div className="grid grid-cols-3 gap-4 text-sm bg-blue-500/10 p-4 rounded">
                                        <div>
                                            <span className="text-slate-400">Tests Completados:</span>
                                            <p className="font-bold text-lg text-white">{results.exhaustiveSearch.summary.testsCompletados}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Exitosos:</span>
                                            <p className="font-bold text-lg text-green-400">{results.exhaustiveSearch.summary.testsExitosos}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Fallidos:</span>
                                            <p className="font-bold text-lg text-red-400">{results.exhaustiveSearch.summary.testsFallidos}</p>
                                        </div>
                                    </div>
                                )}
                                <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                    {JSON.stringify(results.exhaustiveSearch, null, 2)}
                                </pre>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Test Variaciones Result */}
                {results.testVariaciones && (
                    <Card className="bg-white/5 border-white/10">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>🧪 Resultado: Test de Variaciones de Endpoints</CardTitle>
                            <Button
                                onClick={() => exportJSON(results.testVariaciones, 'test_variaciones_existencias')}
                                className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                            >
                                <Download className="w-3 h-3 mr-1" />
                                Exportar JSON
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {results.testVariaciones.summary && (
                                    <div className="grid grid-cols-4 gap-4 text-sm bg-purple-500/10 p-4 rounded">
                                        <div>
                                            <span className="text-slate-400">Variaciones Probadas:</span>
                                            <p className="font-bold text-lg text-white">{results.testVariaciones.summary.variacionesProbadas}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Exitosas:</span>
                                            <p className="font-bold text-lg text-green-400">{results.testVariaciones.summary.variacionesExitosas}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Fallidas:</span>
                                            <p className="font-bold text-lg text-red-400">{results.testVariaciones.summary.variacionesFallidas}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-400">Con Múltiples Bodegas:</span>
                                            <p className="font-bold text-lg text-yellow-400">
                                                {results.testVariaciones.summary.variacionesConMultiplesBodegas?.length || 0}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Destacar variaciones exitosas con múltiples bodegas */}
                                {results.testVariaciones.summary?.variacionesConMultiplesBodegas?.length > 0 && (
                                    <div className="bg-green-500/20 border border-green-500 p-4 rounded">
                                        <h3 className="font-bold text-green-400 mb-2">✅ ENCONTRADO: Variaciones con múltiples bodegas</h3>
                                        <ul className="list-disc list-inside text-sm text-white">
                                            {results.testVariaciones.summary.variacionesConMultiplesBodegas.map((key) => (
                                                <li key={key}>{key}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                    {JSON.stringify(results.testVariaciones, null, 2)}
                                </pre>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Complete Sync Result */}
                {results.completeSync && (
                    <Card className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-green-500/30 border-2">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-green-400 text-xl">🔄 Resultado: Sincronización Completa (TODAS las páginas)</CardTitle>
                            <Button
                                onClick={() => exportJSON(results.completeSync, 'complete_inventory_sync')}
                                className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                            >
                                <Download className="w-3 h-3 mr-1" />
                                Exportar JSON
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {/* Summary Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-green-500/10 p-4 rounded border border-green-500/30">
                                    <div>
                                        <span className="text-slate-400">Páginas Obtenidas:</span>
                                        <p className="font-bold text-2xl text-green-400">{results.completeSync.pagesFetched || 0}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Registros Procesados:</span>
                                        <p className="font-bold text-2xl text-white">{results.completeSync.recordsProcessed || 0}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Sucursales:</span>
                                        <p className="font-bold text-2xl text-blue-400">{results.completeSync.branches?.length || 0}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Omitidos:</span>
                                        <p className="font-bold text-2xl text-red-400">{results.completeSync.recordsSkipped || 0}</p>
                                    </div>
                                </div>

                                {/* Items per Branch */}
                                {results.completeSync.itemsPerBranch && (
                                    <div className="bg-black/40 p-4 rounded">
                                        <h3 className="font-bold text-white mb-3">📊 Items por Sucursal</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {Object.entries(results.completeSync.itemsPerBranch).map(([branch, stats]) => (
                                                <div key={branch} className="bg-slate-800/50 p-3 rounded border border-slate-700">
                                                    <p className="font-bold text-green-400 text-sm mb-2">{branch}</p>
                                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                                        <div>
                                                            <span className="text-slate-400">Total Items:</span>
                                                            <p className="font-bold text-white">{stats.total}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-400">Con Stock:</span>
                                                            <p className="font-bold text-yellow-400">{stats.withStock}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-400">Stock Total:</span>
                                                            <p className="font-bold text-blue-400">{stats.totalStock}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Success Message */}
                                {results.completeSync.success && (
                                    <div className="bg-green-500/20 border border-green-500 p-4 rounded">
                                        <h3 className="font-bold text-green-400 mb-2">✅ SINCRONIZACIÓN COMPLETA EXITOSA</h3>
                                        <p className="text-sm text-white">
                                            Se sincronizaron <strong>{results.completeSync.totalRecordsFetched}</strong> registros
                                            de <strong>{results.completeSync.pagesFetched}</strong> páginas.
                                        </p>
                                        <p className="text-sm text-white mt-2">
                                            Los datos están ahora disponibles en Firebase RTDB en <code className="bg-black/40 px-2 py-1 rounded">/branch_stock</code>
                                        </p>
                                    </div>
                                )}

                                <pre className="bg-black/40 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                                    {JSON.stringify(results.completeSync, null, 2)}
                                </pre>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Instructions */}
                <Card className="bg-blue-500/10 border-blue-500/20">
                    <CardHeader>
                        <CardTitle className="text-blue-400">📋 Instrucciones</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-2 text-slate-300">
                        <p><strong>Botones 1-6:</strong> Tests individuales de diferentes endpoints</p>
                        <p><strong>Botón 7 🔍:</strong> BÚSQUEDA EXHAUSTIVA - Prueba 7 endpoints diferentes en paralelo</p>
                        <p><strong>Botón 8 🧪:</strong> TEST VARIACIONES - Prueba 8 variaciones del endpoint existencias con diferentes parámetros</p>
                        <p><strong>Botón 9 🔄:</strong> 🆕 SYNC COMPLETO - Obtiene TODAS las 99 páginas (4,933 registros) y guarda en Firebase</p>
                        <div className="bg-green-500/20 border border-green-500 p-3 rounded mt-2">
                            <p className="text-green-300 font-bold">⚡ USA EL BOTÓN 9 para sincronizar el inventario completo</p>
                            <p className="text-xs mt-1">Este proceso toma 2-3 minutos y obtiene TODOS los datos de las 4 sucursales</p>
                        </div>
                        <div className="bg-purple-500/20 border border-purple-500 p-3 rounded mt-2">
                            <p className="text-purple-300 font-bold">⚡ EJECUTA EL BOTÓN 8 - Basado en descubrimiento web</p>
                            <p className="text-xs mt-1">Prueba endpoints como /api/inventario/control/existencias-detalladas, parámetros ?detallado=true, ?expand=bodega, y más</p>
                        </div>
                        <p>Abre la consola del navegador (F12) para ver logs detallados</p>
                        <p>Revisa Firebase Functions logs: <code className="bg-black/40 px-2 py-1 rounded">firebase functions:log</code></p>
                        <p className="text-yellow-400 mt-4">⚠️ Esta página es temporal solo para debug. Se eliminará después.</p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
